// Расширение в пути обязательно: модуль загружается не только сборщиком, но и
// node-тестами напрямую (см. tests/gazobeton/msk_gazobeton_scope.test.js).
import { normalizeRegionCode } from "./gazDeliveryRegion.js";

/**
 * Признак разрешённой области разработки: калькулятор МСК → Газобетон.
 *
 * Считается один раз на верхнем уровне (там, где одновременно доступны основные
 * товары и фактический регион расчёта) и передаётся вниз готовым пропсом.
 * Компоненты его не пересчитывают.
 *
 * Что считается доказательством газобетона:
 *   фактическая категория позиции (`product_group`, приходит из поля CATEGORY
 *   номенклатуры: «Газобетон», «Газобетон Bonolit», «Газобетон ЛСР» и т. п.).
 *
 * Что НЕ считается доказательством:
 *   - `category` строки товара: там лежит имя калькулятора («Газобетон и кирпич»),
 *     под которое попадает и кирпич;
 *   - слово «Москва» в адресе — регион берётся кодом;
 *   - принадлежность к группе доставки газобетона: под неё попадают бордюр,
 *     брусчатка, плитка, газонная решётка и лотки.
 *
 * Сопутствующие позиции (клей, U-блок, добавка) добавляются как допродажи и живут
 * в `additionalMaterials` внутри состояния КП. В массив основных товаров они не
 * попадают, поэтому scope смотрит только на основные товары и допродажами не
 * выключается. Если же менеджер выбрал клей самостоятельной строкой в «Товарах»,
 * это уже не заказ газобетона — scope выключается.
 */

const GAZOBETON_CATEGORY_MARKER = "газобетон";

// Фактическая категория позиции. Только product_group: это поле заполняется
// категорией номенклатуры (см. useProductsLogic — product_group: selectedData.CATEGORY).
const factualCategoryOf = (product) => String(
  product?.product_group ?? product?.PRODUCT_GROUP ?? ""
).trim().toLowerCase();

// Строка без выбранного товара — это ещё не позиция заказа.
const isFilledProductRow = (product) => Boolean(
  product && String(product.name || "").trim()
);

/** Позиция основного заказа относится к газобетону. */
export const isGazobetonProductRow = (product) => (
  factualCategoryOf(product).includes(GAZOBETON_CATEGORY_MARKER)
);

// Сопутствующий товар газобетонного заказа — ровно те же признаки, что на сервере
// (routes/mskGazobetonServerScope.js): «клей» и «добавка противоморозная» полным
// совпадением, «u-блок …» префиксом, потому что категория версионируется заводом.
const GAZ_COMPANION_EXACT_CATEGORIES = new Set(["клей", "добавка противоморозная"]);
const GAZ_COMPANION_PREFIX = "u-блок";

/**
 * Сопутствующая позиция основного заказа.
 *
 * 12.10.2026, Роман: «смущает, что вид не такой». Менеджер добавил к газобетону
 * клей отдельной строкой в «Товарах» — и экран переключился с газобетонного расчёта
 * на обычное КП, хотя сервер тот же заказ считал по газобетонным правилам. Клиент
 * требовал, чтобы КАЖДАЯ строка была газобетоном, а сервер пропускал сопутствующие.
 * Два определения одного признака разошлись; клиент приведён к серверному правилу.
 */
export const isGazCompanionProductRow = (product) => {
  const category = factualCategoryOf(product);
  if (!category) return false;
  return GAZ_COMPANION_EXACT_CATEGORIES.has(category) || category.startsWith(GAZ_COMPANION_PREFIX);
};

/**
 * @param {object}  params
 * @param {Array}   params.products — основные выбранные товары («Товары»)
 * @param {string}  params.region   — фактический регион расчёта (код, не адрес)
 * @returns {boolean}
 */
export const getMskGazobetonScope = ({ products, region } = {}) => {
  if (normalizeRegionCode(region) !== "МСК") {
    return false;
  }

  const filledRows = (Array.isArray(products) ? products : []).filter(isFilledProductRow);

  if (filledRows.length === 0) {
    return false;
  }

  // - Правило то же, что на сервере: хотя бы одна позиция газобетона, а остальные —
  //   газобетон или сопутствующий товар. Посторонняя позиция (кирпич, плитка, лоток)
  //   по-прежнему выключает область целиком.
  if (!filledRows.some(isGazobetonProductRow)) {
    return false;
  }

  return filledRows.every((product) => (
    isGazobetonProductRow(product) || isGazCompanionProductRow(product)
  ));
};

export default getMskGazobetonScope;
